import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { AddressMapLink } from "./maps.tsx";
import type { User } from "../shared/contracts.ts";
import type {
  VisitCapability,
  VisitComparison,
  VisitDistribution,
  VisitFreshness,
  VisitMappedField,
  VisitMetrics,
  VisitQuery,
  VisitRecord,
  VisitSource,
  VisitsAccess,
  VisitsConfig,
  VisitsConfigVersion,
  VisitsList,
  VisitsSummary,
  VisitOrigin,
  VisitSnapshot,
  VisitTab,
} from "../shared/visits.ts";
import {
  api,
  apiPDF,
  Banner,
  Button,
  date,
  Empty,
  Field,
  Icon,
  Modal,
  tabKeys,
} from "./ui.tsx";
import { Count, Freshness, VisitSummary } from "./visits-presentation.tsx";
import { VisitsFilters as Filters } from "./visits-filters.tsx";
import { VisitsCompare as Compare } from "./visits-compare.tsx";
import { VisitsReadiness } from "./visits-readiness.tsx";
import {
  formatVisitNumber,
  sortVisitDistribution,
  type VisitDistributionOrder,
} from "./visits-view-model.ts";
import "./visits-overview.css";

type Props = {
  user: User;
  notify?: (message: string) => void;
  onPublished?: () => void;
  onDirty?: (dirty: boolean) => void;
};
type Panel = "summary" | "groups" | "search";
type Dimension = "source" | "group" | "responsible";
type Detail = {
  record: VisitRecord;
  origins: VisitOrigin[];
  freshness: VisitFreshness;
};
type ConfigState = {
  identityStates?: {sourceId:string;state:string;message?:string}[];
  published: VisitsConfig;
  draft?: VisitsConfigVersion | null;
  versions?: VisitsConfigVersion[];
};
const path = "/visits-overview";
const format = formatVisitNumber;
const statusNames = {
  visited: "Visitado",
  pending: "Pendente",
  analysis: "Aguardando análise",
};
const unitCount = (
  value: number | null | undefined,
  singular: string,
  plural: string,
) => `${format(value)} ${value === 1 ? singular : plural}`;
const dimensions = {
  source: "Planilha de origem",
  group: "Grupo",
  responsible: "Responsável atual",
};
function queryString(query: VisitQuery, extra: Record<string, string> = {}) {
  const params = new URLSearchParams(extra);
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== "") params.set(key, String(value));
  });
  return params.toString();
}
const failure = (error: unknown) =>
  error instanceof Error
    ? error.message
    : "Não foi possível consultar. Tente novamente.";
const cancelled = (error: unknown) =>
  error instanceof Error && error.name === "AbortError";
const permissionDenied = (error: unknown) =>
  error instanceof Error &&
  "code" in error &&
  /FORBIDDEN|AUTH|^403$|^401$/.test(String(error.code));
export function visitDate(value: string) {
  return /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? new Intl.DateTimeFormat("pt-BR", {
        dateStyle: "short",
        timeZone: "UTC",
      }).format(new Date(value + "T12:00:00Z"))
    : date(value);
}
const initialQuery: VisitQuery = { page: 1, pageSize: 25 };

function Calculation({ summary }: { summary: VisitsSummary }) {
  const metrics = summary.metrics;
  return (
    <details className="vo-help" id="vo-method">
      <summary>Como é calculado</summary>
      <dl className="vo-definition">
        <dt>Unidade principal</dt>
        <dd>Endereços físicos únicos, consolidados antes da contagem.</dd>
        <dt>Identificação</dt>
        <dd>{metrics.method}</dd>
        <dt>Visitados</dt>
        <dd>
          Endereços únicos com visita válida; ocorrências repetidas não são
          novas visitas.
        </dd>
        <dt>Pendentes</dt>
        <dd>
          Endereços únicos ainda não visitados. Registros sem classificação
          confiável ficam separados em análise.
        </dd>
        <dt>Cobertura</dt>
        <dd>
          Endereços únicos visitados divididos por todos os endereços únicos da
          base, incluindo os aguardando análise, multiplicado por 100. Base
          confirmada vazia exibe 0%; fonte indisponível ou base parcial não
          produz percentual global.
        </dd>
        <dt>Filtros e exclusões</dt>
        <dd>
          O mesmo escopo dos indicadores e das listas. Fontes desativadas e abas
          auxiliares não integram a base.
        </dd>
        <dt>Data de referência</dt>
        <dd>
          {date(summary.freshness.asOf)}.{" "}
          {summary.filters.dateType === "first_visit"
            ? "Período pela primeira visita conhecida."
            : summary.filters.dateType === "latest_visit"
              ? "Período pela última visita conhecida; não representa quantidade de atividades."
              : "Situação atual da base."}
        </dd>
        <dt>Qualidade</dt>
        <dd>
          {metrics.provisional
            ? "Indicadores provisórios: a identificação e as inconsistências ainda exigem conferência."
            : "Consolidação conforme a regra publicada."}
        </dd>
      </dl>
    </details>
  );
}

function RecordLine({
  record,
  access,
  onOpen,
}: {
  record: VisitRecord;
  access: VisitsAccess;
  onOpen: () => void;
}) {
  return (
    <li className="vo-record">
      <button
        className="vo-record-button"
        data-vo-record-id={record.id}
        onClick={onOpen}
        aria-label={`Abrir ${record.street}, ${record.number}`}
      >
        <div className="vo-record-top">
          <div>
            <strong>
              {record.street}, {record.number}
            </strong>
            <p>{record.neighborhood || "Bairro ainda não identificado"}</p>
          </div>
          <Icon name="arrow" size={18} />
        </div>
        <div className="vo-record-status-row">
          <span className={`vo-state ${record.status}`}>
            <Icon
              name={
                record.status === "visited"
                  ? "check"
                  : record.status === "analysis"
                    ? "alert"
                    : "clock"
              }
              size={15}
            />
            {statusNames[record.status]}
          </span>
          {record.origins > 1 && (
            <span className="vo-origin-count">
              {record.sourceIds.length > 1
                ? `${record.sourceIds.length} origens`
                : record.status === "visited"
                  ? `${record.origins} ocorrências · um endereço`
                  : "Ocorrências para conferência"}
            </span>
          )}
        </div>
        {access.permissions.personal && !!record.residents?.length && (
          <p className="vo-resident">{record.residents.join(" · ")}</p>
        )}
        {record.latestVisitAt && (
          <p className="vo-record-meta">
            {record.status === "analysis"
              ? "Data informada na origem, em conferência"
              : "Última visita conhecida"}
            : {visitDate(record.latestVisitAt)}
          </p>
        )}
      </button>
    </li>
  );
}

function QueryChips({
  query,
  access,
  remove,
}: {
  query: VisitQuery;
  access: VisitsAccess;
  remove: (key: keyof VisitQuery) => void;
}) {
  const label: Partial<Record<keyof VisitQuery, string>> = {
    search: "Busca",
    sourceId: "Origem",
    tabId: "Aba",
    groupId: "Grupo",
    subgroupId: "Subgrupo",
    neighborhood: "Bairro",
    responsibleId: "Responsável",
    visitAuthorId: "Autor",
    status: "Situação",
    metricStatus: "Composição",
    dateFrom: "De",
    dateTo: "Até",
    dateType: "Data",
    street: "Rua",
    number: "Número",
    resident: "Morador",
  };
  const named = (key: keyof VisitQuery, value: string) =>
    key === "sourceId"
      ? access.sources.find((item) => item.id === value)?.name || value
      : key === "tabId"
        ? access.sources
            .filter((source) => source.id === query.sourceId)
            .flatMap((item) => item.tabs)
            .find((item) => item.id === value)?.title || value
        : key === "groupId" || key === "subgroupId"
          ? access.groups.find((item) => item.id === value)?.name ||
            (value === "__unmapped__" ? "Sem associação" : value)
          : key === "responsibleId"
            ? access.responsibleNames[value] || value
            : key === "visitAuthorId"
              ? access.authorNames[value] || value
              : key === "status" || key === "metricStatus"
                ? statusNames[value as keyof typeof statusNames]
                : key === "dateType"
                  ? value === "first_visit"
                    ? "Primeira visita"
                    : "Última visita"
                  : value;
  return (
    <div className="vo-filter-chips" aria-label="Filtros aplicados">
      {Object.entries(query)
        .filter(([key, value]) => key in label && !!value)
        .map(([key, value]) => (
          <button
            key={key}
            className="vo-chip"
            onClick={() => remove(key as keyof VisitQuery)}
            aria-label={`Remover filtro ${label[key as keyof VisitQuery]}: ${named(key as keyof VisitQuery, String(value))}`}
          >
            {label[key as keyof VisitQuery]}:{" "}
            {named(key as keyof VisitQuery, String(value))}
            <Icon name="close" size={14} />
          </button>
        ))}
    </div>
  );
}

export function VisitsOverview({ user, notify }: Props) {
  const [access, setAccess] = useState<VisitsAccess | null>(null),
    [bootError, setBootError] = useState("");
  const [panel, setPanel] = useState<Panel>("summary"),
    [query, setQuery] = useState<VisitQuery>(()=>{const responsibleId=new URLSearchParams(location.search).get("responsibleId");return {...initialQuery,...(responsibleId&&responsibleId.length<=160?{responsibleId}:{})};}),
    [search, setSearch] = useState("");
  const [summary, setSummary] = useState<VisitsSummary | null>(null),
    [list, setList] = useState<VisitsList | null>(null),
    [distribution, setDistribution] = useState<VisitDistribution[]>([]);
  const [distributionOrder, setDistributionOrder] =
    useState<VisitDistributionOrder>("pending");
  const [dimension, setDimension] = useState<Dimension>("source"),
    [groupSearch, setGroupSearch] = useState("");
  const [openedGroup, setOpenedGroup] = useState<string | null>(null),
    [groupSources, setGroupSources] = useState<VisitDistribution[]>([]),
    [groupSummary, setGroupSummary] = useState<VisitsSummary | null>(null);
  const groupPosition = useRef(0);
  const groupHeading = useRef<HTMLHeadingElement>(null);
  const returningGroup = useRef<string | null>(null),
    groupBeforeDetail = useRef<string | null>(null),
    searchFocusPending = useRef(false);
  const [loading, setLoading] = useState(false),
    [error, setError] = useState(""),
    [filters, setFilters] = useState(false),
    [compare, setCompare] = useState(false);
  const [selected, setSelected] = useState<string | null>(null),
    [detail, setDetail] = useState<Detail | null>(null),
    [detailError, setDetailError] = useState(""),
    [exporting, setExporting] = useState(false),
    [retry, setRetry] = useState(0);
  const scrollBeforeDetail = useRef(0),
    focusBeforeDetail = useRef<HTMLElement | null>(null),
    recordBeforeDetail = useRef<string | null>(null),
    pageHeading = useRef<HTMLHeadingElement>(null),
    didDetail = useRef(false);
  const blobUrl = useRef<string | null>(null);
  useLayoutEffect(() => {
    if (panel === "groups" && openedGroup) {
      groupHeading.current?.focus({ preventScroll: true });
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [panel, openedGroup]);
  useEffect(() => {
    if (!access) return;
    if (!access.permissions.indicators && access.permissions.records)
      setPanel("search");
    else if (
      !access.permissions.records &&
      panel === "search" &&
      access.permissions.indicators
    )
      setPanel("summary");
    if (!access.permissions.records) {
      setSelected(null);
      setDetail(null);
      setList(null);
    }
    if (!access.permissions.compare || !access.permissions.indicators)
      setCompare(false);
  }, [access, panel]);
  useLayoutEffect(() => {
    if (
      panel === "groups" &&
      !openedGroup &&
      !loading &&
      returningGroup.current
    ) {
      const button = Array.from(
        document.querySelectorAll<HTMLButtonElement>("[data-vo-group-id]"),
      ).find((item) => item.dataset.voGroupId === returningGroup.current);
      if (button) {
        button.focus({ preventScroll: true });
        window.scrollTo({ top: groupPosition.current, behavior: "instant" });
        returningGroup.current = null;
      }
    }
    if (panel === "search" && searchFocusPending.current && !loading && list) {
      const input = document.querySelector<HTMLElement>(".vo-count");
      if (input) {
        input.focus({ preventScroll: true });
        input.scrollIntoView({ block: "center", behavior: "instant" });
        searchFocusPending.current = false;
      }
    }
  }, [panel, openedGroup, distribution, loading, list]);
  const accessSequence = useRef(0);
  useEffect(() => {
    const controller = new AbortController();
    const sequence = ++accessSequence.current;
    setBootError("");
    api<VisitsAccess>(path + "/access", "GET", undefined, controller.signal)
      .then((next) => {
        if (controller.signal.aborted || sequence !== accessSequence.current)
          return;
        setAccess(next);
        if (!next.permissions.indicators && next.permissions.records)
          setPanel("search");
      })
      .catch((error) => {
        if (
          !controller.signal.aborted &&
          sequence === accessSequence.current &&
          !cancelled(error)
        )
          setBootError(failure(error));
      });
    return () => controller.abort();
  }, [user.id, retry]);
  useEffect(() => {
    const timer = setTimeout(
      () =>
        setQuery((previous) =>
          previous.search === search.trim()
            ? previous
            : { ...previous, search: search.trim() || undefined, page: 1 },
        ),
      300,
    );
    return () => clearTimeout(timer);
  }, [search]);
  useEffect(() => {
    let controller: AbortController | null = null;
    const revalidate = () => {
      controller?.abort();
      const currentController = new AbortController();
      controller = currentController;
      const sequence = ++accessSequence.current;
      api<VisitsAccess>(
        path + "/access",
        "GET",
        undefined,
        currentController.signal,
      )
        .then((next) => {
          if (
            currentController.signal.aborted ||
            sequence !== accessSequence.current
          )
            return;
          setAccess(next);
          setBootError("");
        })
        .catch((error) => {
          if (
            !currentController.signal.aborted &&
            sequence === accessSequence.current &&
            !cancelled(error) &&
            permissionDenied(error)
          ) {
            setBootError(failure(error));
            setSummary(null);
            setList(null);
            setDetail(null);
            setDistribution([]);
          }
        });
    };
    window.addEventListener("focus", revalidate);
    window.addEventListener("online", revalidate);
    return () => {
      controller?.abort();
      window.removeEventListener("focus", revalidate);
      window.removeEventListener("online", revalidate);
    };
  }, [user.id]);
  useLayoutEffect(() => {
    if (!access?.permissions.access) return;
    const controller = new AbortController();
    setLoading(true);
    setError("");
    // A new scope must never display the previous scope's totals or records.
    if (panel === "search") setList(null);
    else {
      setDistribution([]);
      if (panel === "groups" && openedGroup) {
        setGroupSummary(null);
        setGroupSources([]);
      } else setSummary(null);
    }
    const params = queryString(query);
    const request =
      panel === "groups" && openedGroup && access.permissions.indicators
        ? Promise.all([
            api<VisitsSummary>(
              path +
                "/summary?" +
                queryString({ ...query, groupId: openedGroup }),
              "GET",
              undefined,
              controller.signal,
            ).then((next) => {
              if (!controller.signal.aborted) setGroupSummary(next);
            }),
            api<VisitDistribution[]>(
              path +
                "/distribution?" +
                queryString(
                  { ...query, groupId: openedGroup },
                  { dimension: "subgroup" },
                ),
              "GET",
              undefined,
              controller.signal,
            ).then((next) => {
              if (!controller.signal.aborted) setDistribution(next);
            }),
            api<VisitDistribution[]>(
              path +
                "/distribution?" +
                queryString(
                  { ...query, groupId: openedGroup },
                  { dimension: "source" },
                ),
              "GET",
              undefined,
              controller.signal,
            ).then((next) => {
              if (!controller.signal.aborted) setGroupSources(next);
            }),
          ])
        : panel === "search" && access.permissions.records
          ? api<VisitsList>(
              path + "/records?" + params,
              "GET",
              undefined,
              controller.signal,
            ).then((next) => {
              if (!controller.signal.aborted) setList(next);
            })
          : access.permissions.indicators
            ? Promise.all([
                api<VisitsSummary>(
                  path + "/summary?" + params,
                  "GET",
                  undefined,
                  controller.signal,
                ).then((next) => {
                  if (!controller.signal.aborted) setSummary(next);
                }),
                api<VisitDistribution[]>(
                  path +
                    "/distribution?" +
                    queryString(query, {
                      dimension: panel === "groups" ? "group" : dimension,
                    }),
                  "GET",
                  undefined,
                  controller.signal,
                ).then((next) => {
                  if (!controller.signal.aborted) setDistribution(next);
                }),
              ])
            : Promise.resolve();
    request
      .catch((error) => {
        if (!controller.signal.aborted && !cancelled(error)) {
          if (permissionDenied(error)) {
            setBootError(failure(error));
            setSummary(null);
            setList(null);
            setDetail(null);
            setDistribution([]);
          } else setError(failure(error));
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [access, panel, query, dimension, retry, openedGroup]);
  useEffect(() => {
    if (!selected || !access?.permissions.records) return;
    const controller = new AbortController();
    setDetail(null);
    setDetailError("");
    api<Detail>(
      path + "/records/" + encodeURIComponent(selected),
      "GET",
      undefined,
      controller.signal,
    )
      .then((next) => {
        if (!controller.signal.aborted) setDetail(next);
      })
      .catch((error) => {
        if (!controller.signal.aborted && !cancelled(error)) {
          if (permissionDenied(error)) {
            setBootError(failure(error));
            setDetail(null);
            setList(null);
          } else setDetailError(failure(error));
        }
      });
    return () => controller.abort();
  }, [selected, access, retry]);
  useLayoutEffect(() => {
    if (selected) {
      didDetail.current = true;
      pageHeading.current?.focus();
      window.scrollTo({ top: 0, behavior: "instant" });
    } else if (didDetail.current) {
      window.scrollTo({ top: scrollBeforeDetail.current, behavior: "instant" });
      // Details unmount the list. Restore the new button by record identity,
      // rather than attempting to focus the disconnected original element.
      const returned = Array.from(
        document.querySelectorAll<HTMLButtonElement>(
          "button[data-vo-record-id]",
        ),
      ).find(
        (button) => button.dataset.voRecordId === recordBeforeDetail.current,
      );
      if (returned) {
        returned.focus({ preventScroll: true });
        didDetail.current = false;
      } else if (list) {
        (
          document.getElementById("vo-tab-search") ||
          (focusBeforeDetail.current?.isConnected
            ? focusBeforeDetail.current
            : null)
        )?.focus({ preventScroll: true });
        didDetail.current = false;
      }
    }
  }, [selected, list]);
  useEffect(
    () => () => {
      if (blobUrl.current) URL.revokeObjectURL(blobUrl.current);
      (window as any).GabineteNumbers?.dispose("visits-overview:" + user.id);
    },
    [user.id],
  );
  function openRecord(id: string) {
    scrollBeforeDetail.current = window.scrollY;
    focusBeforeDetail.current = document.activeElement as HTMLElement;
    recordBeforeDetail.current = id;
    setDetail(null);
    setSelected(id);
  }
  function showRecords(changes: Partial<VisitQuery> = {}) {
    if (!access?.permissions.records) return;
    searchFocusPending.current = true;
    setQuery((previous) => ({ ...previous, ...changes, page: 1 }));
    setPanel("search");
  }
  async function exportPDF() {
    setExporting(true);
    setError("");
    try {
      // Export the group currently being read, with the same filters as its totals.
      const exportQuery =
        panel === "groups" && openedGroup
          ? { ...query, groupId: openedGroup }
          : query;
      const blob = await apiPDF(path + "/export", { query: exportQuery });
      if (blobUrl.current) URL.revokeObjectURL(blobUrl.current);
      blobUrl.current = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = blobUrl.current;
      anchor.download = "visao-geral-das-visitas.pdf";
      anchor.click();
      notify?.("PDF gerado com os filtros e as permissões atuais.");
    } catch (error) {
      setError(failure(error));
    } finally {
      setExporting(false);
    }
  }
  if (bootError)
    return (
      <section className="visits-overview">
        <h1>Visão geral das visitas</h1>
        <Banner kind="danger">{bootError}</Banner>
        <Button onClick={() => setRetry((value) => value + 1)}>
          Tentar novamente
        </Button>
      </section>
    );
  if (!access)
    return (
      <section className="visits-overview" aria-busy="true">
        <h1>Visão geral das visitas</h1>
        <p className="vo-loading" role="status">
          Conferindo seu acesso…
        </p>
      </section>
    );
  if (!access.permissions.access)
    return (
      <Empty title="Acesso não autorizado" icon="users">
        A consulta consolidada depende de autorização individual do
        administrador.
      </Empty>
    );
  if (selected && access.permissions.records)
    return (
      <section className="visits-overview vo-detail">
        <Button icon="back" onClick={() => setSelected(null)}>
          Voltar aos resultados
        </Button>
        <h1 ref={pageHeading} tabIndex={-1}>
          Endereço
        </h1>
        {detailError ? (
          <>
            <Banner kind="danger">{detailError}</Banner>
            <Button onClick={() => setRetry((value) => value + 1)}>
              Tentar novamente
            </Button>
          </>
        ) : !detail ? (
          <p role="status">Consultando endereço…</p>
        ) : (
          <>
            <div className="vo-surface">
              <h2>
                {detail.record.street}, {detail.record.number}
              </h2>
              <p>
                {detail.record.neighborhood || "Bairro ainda não identificado"}
              </p>
              <span className={`vo-state ${detail.record.status}`}>
                {statusNames[detail.record.status]}
              </span>
              {detail.origins.length > 1 && (
                <p className="vo-record-meta">
                  Um endereço físico com {detail.origins.length} ocorrências de
                  origem. As ocorrências não representam visitas adicionais.
                </p>
              )}
              <dl className="vo-definition">
                {access.permissions.personal && (
                  <>
                    <dt>Moradores</dt>
                    <dd>
                      {detail.record.residents?.join(" · ") || "Não informado"}
                    </dd>
                    {!!detail.record.phones?.length && (
                      <>
                        <dt>Contatos</dt>
                        <dd>{detail.record.phones.join(" · ")}</dd>
                      </>
                    )}
                  </>
                )}
                <dt>Grupo</dt>
                <dd>
                  {detail.record.groupIds
                    .map(
                      (id) =>
                        access.groups.find((group) => group.id === id)?.name ||
                        id,
                    )
                    .join(" · ") || "Sem grupo"}
                </dd>
                <dt>Subgrupo</dt>
                <dd>
                  {detail.record.subgroupIds
                    .map(
                      (id) =>
                        access.groups.find((group) => group.id === id)?.name ||
                        id,
                    )
                    .join(" · ") || "Sem subgrupo"}
                </dd>
                <dt>Responsável atual</dt>
                <dd>
                  {detail.record.responsibleIds
                    .map(
                      (id) =>
                        access.responsibleNames[id] ||
                        "Responsável não identificado",
                    )
                    .join(" · ") || "Associação ainda não confirmada"}
                </dd>
                <dt>Autor da visita</dt>
                <dd>
                  {detail.record.visitAuthorIds
                    .map(
                      (id) =>
                        access.authorNames[id] || "Autor não identificado",
                    )
                    .join(" · ") || "Não identificado"}
                </dd>
                <dt>
                  {detail.record.status === "analysis"
                    ? "Primeira data informada, em conferência"
                    : "Primeira visita conhecida"}
                </dt>
                <dd>
                  {detail.record.firstVisitAt
                    ? visitDate(detail.record.firstVisitAt)
                    : "Data não disponível. A última visita não substitui esta informação."}
                </dd>
                <dt>
                  {detail.record.status === "analysis"
                    ? "Última data informada, em conferência"
                    : "Última visita conhecida"}
                </dt>
                <dd>
                  {detail.record.latestVisitAt
                    ? visitDate(detail.record.latestVisitAt)
                    : "Sem data disponível"}
                </dd>
              </dl>
              {detail.record.issues.length > 0 && (
                <Banner kind="warn">{detail.record.issues.join(" · ")}</Banner>
              )}
              <p className="muted">
                Esta área é de consulta. Ela não concede autorização para
                alterar o endereço ou registrar uma visita.
              </p>
              <AddressMapLink address={detail.record} />
            </div>
            <details className="vo-help">
              <summary>Origem do registro ({detail.origins.length})</summary>
              {detail.origins.map((origin) => (
                <article
                  className="vo-origin"
                  key={`${origin.sourceId}:${origin.tabId}:${origin.externalId}`}
                >
                  <strong>{origin.sourceName}</strong>
                  <dl className="vo-definition">
                    <dt>Aba</dt>
                    <dd>{origin.tabTitle}</dd>
                    <dt>Localização na origem</dt>
                    <dd>
                      {origin.row
                        ? `Linha ${origin.row} na última leitura`
                        : "Posição não disponível"}
                    </dd>
                    <dt>Última leitura</dt>
                    <dd>{date(origin.synchronizedAt)}</dd>
                    <dt>Identificador</dt>
                    <dd>{origin.externalId}</dd>
                  </dl>
                  <small>
                    A linha pode mudar; ela não identifica permanentemente o
                    endereço.
                  </small>
                </article>
              ))}
            </details>
            <Freshness value={detail.freshness} access={access} />
          </>
        )}
      </section>
    );
  const freshness =
    panel === "search"
      ? list?.freshness
      : panel === "groups" && openedGroup
        ? groupSummary?.freshness
        : summary?.freshness;
  const metrics = summary?.metrics;
  const filtered = Object.entries(query).filter(
    ([key, value]) => !["page", "pageSize", "search"].includes(key) && !!value,
  ).length;
  return (
    <section className="visits-overview">
      <div className="vo-head">
        <div>
          <h1
            ref={groupHeading}
            tabIndex={openedGroup && panel === "groups" ? -1 : undefined}
          >
            {panel === "groups" && openedGroup
              ? access.groups.find((group) => group.id === openedGroup)?.name ||
                "Sem grupo confirmado"
              : "Visão geral das visitas"}
          </h1>
          <p>
            {query.sourceId
              ? access.sources.find((source) => source.id === query.sourceId)
                  ?.name
              : "Todas as origens"}
          </p>
        </div>
        <div className="vo-tools">
          <Button icon="settings" onClick={() => setFilters(true)}>
            Filtrar{filtered ? ` (${filtered})` : ""}
          </Button>
        </div>
      </div>
      {!navigator.onLine && (
        <Banner kind="warn">
          Sem conexão. Esta área ainda não possui cópia offline autorizada. Os
          dados já abertos podem estar desatualizados.
        </Banner>
      )}
      <div
        className="vo-tabs"
        role="tablist"
        aria-label="Visão geral das visitas"
        onKeyDown={tabKeys}
      >
        {(
          [
            ["summary", "Resumo", access.permissions.indicators],
            ["groups", "Grupos", access.permissions.indicators],
            ["search", "Buscar", access.permissions.records],
          ] as const
        )
          .filter((item) => item[2])
          .map(([id, name]) => (
            <button
              key={id}
              id={`vo-tab-${id}`}
              role="tab"
              aria-selected={panel === id}
              aria-controls="vo-panel"
              tabIndex={panel === id ? 0 : -1}
              onClick={() => setPanel(id)}
            >
              {name}
            </button>
          ))}
      </div>
      <Freshness value={freshness} access={access} />
      <QueryChips
        query={query}
        access={access}
        remove={(key) => {
          if (key === "search") setSearch("");
          setQuery((previous) => ({
            ...previous,
            [key]: undefined,
            ...(key === "sourceId" ? { tabId: undefined } : {}),
            ...(key === "groupId" ? { subgroupId: undefined } : {}),
            ...(key === "dateType"
              ? { dateFrom: undefined, dateTo: undefined }
              : {}),
            page: 1,
          }));
        }}
      />
      {error && (
        <div className="vo-error">
          <Banner kind="danger">{error} Os filtros foram preservados.</Banner>
          <Button onClick={() => setRetry((value) => value + 1)}>
            Tentar novamente
          </Button>
        </div>
      )}
      <div
        id="vo-panel"
        role="tabpanel"
        aria-labelledby={`vo-tab-${panel}`}
        aria-busy={loading}
      >
        {loading && (
          <p role="status" className="vo-loading">
            Atualizando a consulta…
          </p>
        )}
        {panel === "summary" && access.permissions.indicators && (
          <>
            <VisitSummary
              metrics={metrics}
              freshness={summary?.freshness}
              loading={loading}
              canOpen={access.permissions.records}
              onOpen={showRecords}
              scope={`visits-overview:${user.id}`}
            />
            <div className="vo-surface vo-distribution-surface">
              <div className="vo-compact-toolbar">
                <h2>
                  {dimension === "source"
                    ? "Por planilha de origem"
                    : dimension === "group"
                      ? "Por grupo"
                      : "Por responsável atual"}
                </h2>
                <Field label="Agrupar por">
                  <select
                    value={dimension}
                    onChange={(event) =>
                      setDimension(event.target.value as Dimension)
                    }
                  >
                    <option value="source">Planilha de origem</option>
                    <option value="group">Grupo</option>
                    {Object.keys(access.responsibleNames).length > 0 && (
                      <option value="responsible">Responsável atual</option>
                    )}
                  </select>
                </Field>
                <Field label="Ordenar">
                  <select
                    value={distributionOrder}
                    onChange={(event) =>
                      setDistributionOrder(
                        event.target.value as VisitDistributionOrder,
                      )
                    }
                  >
                    <option value="pending">Mais pendentes</option>
                    <option value="coverage_desc">Maior cobertura</option>
                    <option value="coverage_asc">Menor cobertura</option>
                    <option value="name">Nome</option>
                  </select>
                </Field>
                {access.permissions.compare && (
                  <Button kind="ghost" onClick={() => setCompare(true)}>
                    Comparar
                  </Button>
                )}
              </div>
              <p className="vo-overlap">
                <Icon name="info" size={16} />
                <span>
                  {dimension === "source"
                    ? "Um endereço pode aparecer em mais de uma origem. O total geral considera cada endereço uma única vez."
                    : "Os recortes podem compartilhar endereços. Seus subtotais não devem ser somados para obter o total geral."}
                </span>
              </p>
              <Distribution
                compact
                items={sortVisitDistribution(distribution, distributionOrder)}
                access={access}
                loading={loading}
                onSelect={(id) =>
                  showRecords({
                    [dimension === "source"
                      ? "sourceId"
                      : dimension === "group"
                        ? "groupId"
                        : "responsibleId"]: id,
                  })
                }
              />
            </div>
            {summary && (
              <>
                <details className="vo-help">
                  <summary>Qualidade e registros de origem</summary>
                  <dl className="vo-definition">
                    <dt>Registros de origem</dt>
                    <dd>{format(metrics?.sourceRecords)}</dd>
                    <dt>Ocorrências duplicadas</dt>
                    <dd>{format(metrics?.duplicateOccurrences)}</dd>
                  </dl>
                  <p>
                    Contagens de linhas não representam cidadãos únicos.
                    Importar e consultar dados não concede pontos ou XP.
                  </p>
                </details>
                <Calculation summary={summary} />
              </>
            )}
          </>
        )}
        {panel === "groups" && access.permissions.indicators && (
          <div className="vo-surface">
            {openedGroup ? (
              <>
                <Button
                  icon="back"
                  onClick={() => {
                    returningGroup.current = groupBeforeDetail.current;
                    setOpenedGroup(null);
                  }}
                >
                  Voltar aos grupos
                </Button>
                <p>
                  {unitCount(
                    groupSummary?.metrics.uniqueAddresses,
                    "endereço único",
                    "endereços únicos",
                  )}{" "}
                  ·{" "}
                  {unitCount(
                    groupSummary?.metrics.visited,
                    "visitado",
                    "visitados",
                  )}{" "}
                  ·{" "}
                  {unitCount(
                    groupSummary?.metrics.pending,
                    "pendente",
                    "pendentes",
                  )}{" "}
                  ·{" "}
                  {unitCount(
                    groupSummary?.metrics.analysis,
                    "em análise",
                    "em análise",
                  )}
                  {groupSummary?.metrics.coverage != null && (
                    <>
                      {" "}
                      · {format(groupSummary.metrics.coverage, 1)}% de cobertura
                    </>
                  )}
                </p>
                <Button
                  disabled={!access.permissions.records || loading}
                  onClick={() =>
                    showRecords({ groupId: openedGroup, subgroupId: undefined })
                  }
                >
                  Ver endereços do grupo
                </Button>
                <h3 className="vo-help">Subgrupos</h3>
                <Distribution
                  items={distribution}
                  access={access}
                  loading={loading}
                  onSelect={(id) =>
                    showRecords({ groupId: openedGroup, subgroupId: id })
                  }
                />
                <details className="vo-help">
                  <summary>Distribuição entre fontes</summary>
                  <p className="vo-overlap">
                    Um endereço pode aparecer em mais de uma origem. O total do
                    grupo considera cada endereço uma única vez.
                  </p>
                  <Distribution
                    items={groupSources}
                    access={access}
                    loading={loading}
                    onSelect={(id) =>
                      showRecords({ groupId: openedGroup, sourceId: id })
                    }
                  />
                </details>
                {groupSummary && <Calculation summary={groupSummary} />}
              </>
            ) : (
              <>
                <Field label="Buscar grupo">
                  <input
                    type="search"
                    value={groupSearch}
                    onChange={(event) => setGroupSearch(event.target.value)}
                  />
                </Field>
                <p className="vo-overlap">
                  Os grupos podem compartilhar endereços. O total geral
                  considera cada endereço uma única vez.
                </p>
                <Distribution
                  items={distribution.filter((item) =>
                    item.name
                      .normalize("NFD")
                      .replace(/[\u0300-\u036f]/g, "")
                      .toLocaleLowerCase("pt-BR")
                      .includes(
                        groupSearch
                          .normalize("NFD")
                          .replace(/[\u0300-\u036f]/g, "")
                          .toLocaleLowerCase("pt-BR"),
                      ),
                  )}
                  access={access}
                  loading={loading}
                  onSelect={(id) => {
                    groupPosition.current = window.scrollY;
                    groupBeforeDetail.current = id;
                    setOpenedGroup(id);
                    setGroupSummary(null);
                    setDistribution([]);
                  }}
                  canOpen={access.permissions.indicators}
                />
              </>
            )}
          </div>
        )}
        {panel === "search" && access.permissions.records && (
          <>
            <form
              className="vo-search"
              onSubmit={(event) => {
                event.preventDefault();
                setQuery((previous) => ({
                  ...previous,
                  search: search.trim() || undefined,
                  page: 1,
                }));
              }}
            >
              <Field
                label={
                  access.permissions.personal
                    ? "Buscar rua, número ou morador"
                    : "Buscar rua ou número"
                }
              >
                <input
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  autoComplete="off"
                />
              </Field>
              <Button
                type="submit"
                icon="search"
                aria-label="Buscar endereço"
              />
            </form>
            <p className="vo-count" role="status" tabIndex={-1}>
              {list &&
              !loading &&
              list.freshness.state !== "unavailable" &&
              list.freshness.availableSources > 0
                ? `${format(list.total)} ${list.total === 1 ? "endereço encontrado" : "endereços encontrados"}${list.freshness.state === "partial" ? " nos dados disponíveis" : ""}`
                : ""}
            </p>
            {list?.items.length ? (
              <div className="vo-surface">
                <ul className="vo-list">
                  {list.items.map((record) => (
                    <RecordLine
                      key={record.id}
                      record={record}
                      access={access}
                      onOpen={() => openRecord(record.id)}
                    />
                  ))}
                </ul>
              </div>
            ) : (
              !loading &&
              !!list &&
              !error && (
                <Empty
                  title={
                    list && list.freshness.availableSources === 0
                      ? "Dados ainda indisponíveis"
                      : access.sources.length
                        ? "Nenhum endereço encontrado"
                        : "Nenhuma fonte disponível"
                  }
                  icon="search"
                >
                  {list && list.freshness.availableSources === 0
                    ? "As fontes precisam concluir uma sincronização válida antes da busca. Uma fonte indisponível não significa que ela esteja vazia."
                    : access.sources.length
                      ? "Experimente outra rua ou ajuste os filtros."
                      : "O administrador precisa conectar, mapear e sincronizar as fontes antes da consulta."}
                </Empty>
              )
            )}
            {list && list.total > list.pageSize && (
              <nav className="vo-pagination" aria-label="Páginas dos endereços">
                <Button
                  disabled={loading || list.page <= 1}
                  onClick={() =>
                    setQuery((previous) => ({
                      ...previous,
                      page: Math.max(1, (previous.page || 1) - 1),
                    }))
                  }
                >
                  Anterior
                </Button>
                <span>
                  Página {list.page} de {Math.ceil(list.total / list.pageSize)}
                </span>
                <Button
                  disabled={loading || list.page * list.pageSize >= list.total}
                  onClick={() =>
                    setQuery((previous) => ({
                      ...previous,
                      page: (previous.page || 1) + 1,
                    }))
                  }
                >
                  Próxima
                </Button>
              </nav>
            )}
          </>
        )}
      </div>
      {access.permissions.export && (
        <Button
          icon="download"
          busy={exporting}
          disabled={loading || !freshness || freshness.state === "unavailable"}
          onClick={exportPDF}
        >
          Exportar PDF
        </Button>
      )}
      {filters && (
        <Filters
          value={query}
          access={access}
          onClose={() => setFilters(false)}
          onApply={(next) => {
            setQuery(next);
            setSearch(next.search || "");
            setOpenedGroup(null);
            setFilters(false);
          }}
        />
      )}
      {compare && (
        <Compare
          query={query}
          access={access}
          onClose={() => setCompare(false)}
        />
      )}
    </section>
  );
}

function Distribution({
  items,
  access,
  loading,
  onSelect,
  canOpen,
  compact = false,
}: {
  items: VisitDistribution[];
  access: VisitsAccess;
  loading: boolean;
  onSelect: (id: string) => void;
  canOpen?: boolean;
  compact?: boolean;
}) {
  if (!items.length && !loading)
    return <p className="muted">Não há dados disponíveis neste agrupamento.</p>;
  return (
    <ul className={compact ? "vo-list vo-distribution-grid" : "vo-list"}>
      {items.map((item) => (
        <li key={item.id}>
          <button
            className="vo-distribution"
            data-vo-group-id={item.id}
            onClick={() => onSelect(item.id)}
            disabled={!(canOpen ?? access.permissions.records) || loading}
          >
            <div>
              <div className="vo-distribution-title">
                <strong>{item.name}</strong>
                <span>
                  {item.metrics.coverage == null
                    ? "Cobertura indisponível"
                    : format(item.metrics.coverage, 1) + "%"}
                  <small>
                    {item.metrics.coverage != null ? "de cobertura" : ""}
                  </small>
                </span>
              </div>
              <p className="vo-distribution-total">
                {compact ? (
                  <>
                    <strong>{format(item.metrics.uniqueAddresses)}</strong>
                    <span>endereços</span>
                  </>
                ) : (
                  unitCount(
                    item.metrics.uniqueAddresses,
                    "endereço",
                    "endereços",
                  )
                )}
              </p>
              {item.metrics.coverage != null && (
                <progress
                  className="vo-progress"
                  value={item.metrics.coverage}
                  max={100}
                  aria-label={`Cobertura de ${item.name}: ${format(item.metrics.coverage, 1)}%`}
                />
              )}
              {compact ? (
                <dl className="vo-card-counts">
                  <div>
                    <dt>Visitados</dt>
                    <dd>{format(item.metrics.visited)}</dd>
                  </div>
                  <div>
                    <dt>Pendentes</dt>
                    <dd>{format(item.metrics.pending)}</dd>
                  </div>
                  <div>
                    <dt>Em análise</dt>
                    <dd>{format(item.metrics.analysis)}</dd>
                  </div>
                </dl>
              ) : (
                <p className="vo-distribution-counts">
                  {unitCount(item.metrics.visited, "visitado", "visitados")} ·{" "}
                  {unitCount(item.metrics.pending, "pendente", "pendentes")}
                  {item.metrics.analysis == null
                    ? " · Análise indisponível"
                    : item.metrics.analysis > 0
                      ? ` · ${format(item.metrics.analysis)} em análise`
                      : ""}
                </p>
              )}
              {!!item.responsibleIds?.length && (
                <small>
                  {item.responsibleIds.length === 1
                    ? "Responsável pelos endereços: "
                    : "Responsáveis pelos endereços: "}
                  {item.responsibleIds
                    .map(
                      (id) =>
                        access.responsibleNames[id] ||
                        "Associação não identificada",
                    )
                    .join(" · ")}
                </small>
              )}
            </div>
            <span className="vo-open-row">
              <Icon name="arrow" size={18} />
              <span className="sr-only">Abrir {item.name}</span>
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

export default VisitsOverview;

const capabilityNames: Record<VisitCapability, string> = {
  access: "Acessar a visão consolidada",
  indicators: "Ver indicadores",
  records: "Consultar endereços",
  personal: "Ver nomes e contatos pessoais",
  compare: "Comparar resultados",
  export: "Exportar PDF",
  configure: "Configurar fontes e indicadores",
};
const mappedFieldNames: Record<VisitMappedField, string> = {
  id: "Identificador estável da origem",
  street: "Rua",
  number: "Número",
  neighborhood: "Bairro",
  resident: "Morador",
  residentPhone: "Celular",
  group: "Grupo",
  subgroup: "Subgrupo",
  responsibleId: "Responsável atual (identificador)",
  visitAuthorId: "Autor da visita (identificador)",
  firstVisitAt: "Primeira visita conhecida",
  latestVisitAt: "Última visita conhecida",
  status: "Situação",
  positiveLegacy: "POSITIVO — legado sem efeito operacional",
};

export function VisitsOverviewAdmin({
  user,
  notify,
  onPublished,
  onDirty,
}: Props) {
  const [state, setState] = useState<ConfigState | null>(null),
    [config, setConfig] = useState<VisitsConfig | null>(null),
    [versions, setVersions] = useState<VisitsConfigVersion[]>([]);
  const [users, setUsers] = useState<
      Array<{ id: string; name: string; admin: boolean; active: boolean }>
    >([]),
    [error, setError] = useState(""),
    [busy, setBusy] = useState("");
  const [dirty, setDirty] = useState(false),
    [validation, setValidation] = useState<
      Array<{ code: string; message: string }>
    >([]),
    [validated, setValidated] = useState(false),
    [section, setSection] = useState("sources");
  const [configSearch, setConfigSearch] = useState("");
  const [snapshotName, setSnapshotName] = useState("");
  const [conflict, setConflict] = useState(false);
  useEffect(() => {
    onDirty?.(dirty);
  }, [dirty, onDirty]);
  useEffect(() => () => onDirty?.(false), [onDirty]);
  const update = (fn: (next: VisitsConfig) => void) => {
    if (!config) return;
    const next = structuredClone(config);
    fn(next);
    setConfig(next);
    setDirty(true);
    setValidated(false);
    setValidation([]);
  };
  async function load() {
    const next = await api<ConfigState>(path + "/config");
    setState(next);
    setConfig(structuredClone(next.draft?.config || next.published));
    setVersions(next.versions || []);
    setDirty(false);
    setValidated(false);
  }
  useEffect(() => {
    const controller = new AbortController();
    api<ConfigState>(path + "/config", "GET", undefined, controller.signal)
      .then((next) => {
        if (controller.signal.aborted) return;
        setState(next);
        setConfig(structuredClone(next.draft?.config || next.published));
        setVersions(next.versions || []);
      })
      .catch((error) => {
        if (!controller.signal.aborted && !cancelled(error))
          setError(failure(error));
      });
    api<Array<{ id: string; name: string; admin: boolean; active: boolean }>>(
      path + "/users",
      "GET",
      undefined,
      controller.signal,
    )
      .then((next) => {
        if (!controller.signal.aborted) setUsers(next);
      })
      .catch((error) => {
        if (!controller.signal.aborted && !cancelled(error))
          setError(failure(error));
      });
    return () => controller.abort();
  }, [user.id, user.admin]);
  useEffect(() => {
    const unload = (event: BeforeUnloadEvent) => {
      if (dirty) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", unload);
    return () => window.removeEventListener("beforeunload", unload);
  }, [dirty]);
  async function run(name: string, action: () => Promise<void>) {
    setBusy(name);
    setError("");
    setConflict(false);
    try {
      await action();
    } catch (error) {
      setError(failure(error));
      setConflict(
        error instanceof Error &&
          "code" in error &&
          /CONFLICT/.test(String(error.code)),
      );
    } finally {
      setBusy("");
    }
  }
  if (!state || !config)
    return (
      <section className="visits-overview-admin">
        <h1>Fontes das visitas</h1>
        {error ? (
          <>
            <Banner kind="danger">{error}</Banner>
            <Button onClick={() => run("load", load)}>Tentar novamente</Button>
          </>
        ) : (
          <p role="status">Carregando configurações…</p>
        )}
      </section>
    );
  const sourceUpdate = (index: number, fn: (source: VisitSource) => void) =>
    update((next) => fn(next.sources[index]));
  const currentVersion = state.draft?.version || state.published.version;
  return (
    <section className="visits-overview visits-overview-admin">
      <div className="vo-head">
        <div>
          <h1>Fontes das visitas</h1>
          <p>Mapeamento, acesso e atualização da consulta consolidada.</p>
        </div>
      </div>
      {error && (
        <Banner kind="danger">
          {error} As alterações desta tela foram preservadas.
          {conflict && (
            <p>
              <Button
                kind="danger-ghost"
                disabled={!!busy}
                onClick={() => run("load", load)}
              >
                Descartar minhas alterações e recarregar
              </Button>
            </p>
          )}
        </Banner>
      )}
      <div className="vo-admin-status">
        <span>Publicada: versão {state.published.version}</span>
        {state.draft && <span>Rascunho: versão {state.draft.version}</span>}
        {dirty && <strong>Alterações não salvas</strong>}
      </div>
      {state.identityStates?.filter(s=>s.state==='pending').map(s=><Banner key={s.sourceId} kind="warning">{config.sources.find(source=>source.id===s.sourceId)?.name??'Fonte'}: identificação permanente pendente. {s.message} Os endereços continuam na consulta; não receberam recompensas por esta preparação.</Banner>)}
      <div className="vo-tools">
        <Button
          icon="save"
          busy={busy === "save"}
          disabled={!dirty || !!busy}
          onClick={() =>
            run("save", async () => {
              const prepared = structuredClone(config);
              for (const source of prepared.sources)
                for (const tab of source.tabs) {
                  if (tab.statusValues)
                    for (const key of [
                      "visited",
                      "pending",
                      "analysis",
                    ] as const)
                      tab.statusValues[key] = tab.statusValues[key]
                        .map((value) => value.trim())
                        .filter(Boolean);
                }
              await api(path + "/config/draft", "PUT", {
                config: prepared,
                expectedVersion: state.published.version,
                expectedDraftAt: state.draft?.at ?? null,
              });
              await load();
              notify?.(
                "Rascunho salvo. As fontes publicadas continuam em uso.",
              );
            })
          }
        >
          Salvar rascunho
        </Button>
        <Button
          disabled={dirty || !state.draft || !!busy}
          busy={busy === "validate"}
          onClick={() =>
            run("validate", async () => {
              const result = await api<{
                issues: Array<{ code: string; message: string }>;
                valid: boolean;
              }>(path + "/config/validate", "POST");
              setValidation(result.issues || []);
              setValidated(result.valid);
              notify?.(
                result.valid
                  ? "Configuração validada."
                  : "Confira os problemas do mapeamento.",
              );
            })
          }
        >
          Validar mapeamento
        </Button>
        <Button
          kind="primary"
          disabled={dirty || !state.draft || !validated || !!busy}
          busy={busy === "publish"}
          onClick={() =>
            run("publish", async () => {
              await api(path + "/config/publish", "POST", {
                expectedVersion: state.published.version,
                expectedDraftAt: state.draft?.at ?? null,
              });
              await load();
              onPublished?.();
              notify?.(
                "Configuração publicada. A consulta usará a nova versão.",
              );
            })
          }
        >
          Publicar
        </Button>
      </div>
      <p className="vo-help muted">
        As mudanças entram em vigor após salvar, validar e publicar. Conectar
        uma fonte não concede poderes para editar sua planilha.
      </p>
      {validation.length > 0 && (
        <Banner kind="warn">
          <ul>
            {validation.map((issue, index) => (
              <li key={index}>{issue.message}</li>
            ))}
          </ul>
        </Banner>
      )}
      {validated && (
        <Banner kind="success">
          Mapeamento validado. A disponibilidade dos dados depende da conexão e
          da sincronização de cada fonte.
        </Banner>
      )}
      <div className="vo-section-head">
        <Field label="Pesquisar configurações">
          <input
            type="search"
            value={configSearch}
            onChange={(event) => setConfigSearch(event.target.value)}
            placeholder="Fonte, acesso, grupo…"
          />
        </Field>
        <Field label="Assunto">
          <select
            value={section}
            onChange={(event) => setSection(event.target.value)}
          >
            <option value="sources">Fontes e mapeamento</option>
            <option value="groups">Grupos e associações</option>
            {user.admin && (
              <option value="permissions">Pessoas e permissões</option>
            )}
            <option value="updates">Atualização e limites</option>
            {user.admin && (
              <option value="readiness">Preparar delegações</option>
            )}
            <option value="versions">Versões e comparação</option>
          </select>
        </Field>
      </div>
      <fieldset disabled={!!busy} style={{ border: 0, padding: 0, margin: 0 }}>
        {section === "readiness" && user.admin && (
          <VisitsReadiness config={state.published} />
        )}
        {section === "sources" && (
          <div className="vo-surface">
            <h2>Fontes conectadas</h2>
            <p className="muted">
              Use apenas abas originais. GERAL, BASE_BUSCA e relatórios
              derivados não devem ser somados outra vez.
            </p>
            {config.sources
              .filter(
                (source) =>
                  !configSearch ||
                  `${source.name} ${source.spreadsheetId}`
                    .toLocaleLowerCase("pt-BR")
                    .includes(configSearch.toLocaleLowerCase("pt-BR")),
              )
              .map((source) => {
                const index = config.sources.findIndex(
                  (item) => item.id === source.id,
                );
                return (
                  <details
                    className="vo-admin-source"
                    key={source.id}
                    open={!source.name}
                  >
                    <summary>
                      {source.name || "Nova fonte"} ·{" "}
                      {source.enabled ? "incluída" : "desativada"}
                    </summary>
                    <Field label="Nome de exibição">
                      <input
                        value={source.name}
                        onChange={(event) =>
                          sourceUpdate(index, (item) => {
                            item.name = event.target.value;
                          })
                        }
                      />
                    </Field>
                    <Field label="Identificador da planilha Google">
                      <input
                        value={source.spreadsheetId}
                        onChange={(event) =>
                          sourceUpdate(index, (item) => {
                            item.spreadsheetId = event.target.value;
                          })
                        }
                        autoComplete="off"
                      />
                    </Field>
                    <label className="vo-admin-toggle">
                      <input
                        type="checkbox"
                        checked={source.enabled}
                        onChange={(event) =>
                          sourceUpdate(index, (item) => {
                            item.enabled = event.target.checked;
                          })
                        }
                      />
                      <span>
                        Incluir na consolidação
                        <small>
                          Desativar preserva o cadastro para uma publicação
                          posterior.
                        </small>
                      </span>
                    </label>
                    <div className="vo-mapping-grid">
                      <Field label="Atualizar a cada (minutos)">
                        <input
                          type="number"
                          min={5}
                          max={10080}
                          value={source.syncIntervalMinutes}
                          onChange={(event) =>
                            sourceUpdate(index, (item) => {
                              item.syncIntervalMinutes = Number(
                                event.target.value,
                              );
                            })
                          }
                        />
                      </Field>
                      <Field label="Limite de linhas por aba">
                        <input
                          type="number"
                          min={1}
                          max={100000}
                          value={source.maxRowsPerTab}
                          onChange={(event) =>
                            sourceUpdate(index, (item) => {
                              item.maxRowsPerTab = Number(event.target.value);
                            })
                          }
                        />
                      </Field>
                      <Field label="Limite de consultas Google por atualização">
                        <input
                          type="number"
                          min={1}
                          max={5000}
                          value={source.maxRequestsPerSync ?? 300}
                          onChange={(event) =>
                            sourceUpdate(index, (item) => {
                              item.maxRequestsPerSync = Number(
                                event.target.value,
                              );
                            })
                          }
                        />
                      </Field>
                    </div>
                    {source.tabs.map((tab, tabIndex) => (
                      <details
                        className="vo-admin-source"
                        key={tab.id}
                        open={!tab.title}
                      >
                        <summary>{tab.title || "Nova aba original"}</summary>
                        <div className="vo-mapping-grid">
                          <Field label="Nome da aba">
                            <input
                              value={tab.title}
                              onChange={(event) =>
                                sourceUpdate(index, (item) => {
                                  item.tabs[tabIndex].title =
                                    event.target.value;
                                })
                              }
                            />
                          </Field>
                          <Field label="Identificador da aba (gid)">
                            <input
                              type="number"
                              min={0}
                              value={tab.sheetId}
                              onChange={(event) =>
                                sourceUpdate(index, (item) => {
                                  item.tabs[tabIndex].sheetId = Number(
                                    event.target.value,
                                  );
                                })
                              }
                            />
                          </Field>
                          <Field label="Linha dos cabeçalhos">
                            <input
                              type="number"
                              min={1}
                              value={tab.headerRow}
                              onChange={(event) =>
                                sourceUpdate(index, (item) => {
                                  item.tabs[tabIndex].headerRow = Number(
                                    event.target.value,
                                  );
                                })
                              }
                            />
                          </Field>
                          <Field label="Grupo confirmado da aba">
                            <select
                              value={tab.groupId || ""}
                              onChange={(event) =>
                                sourceUpdate(index, (item) => {
                                  item.tabs[tabIndex].groupId =
                                    event.target.value || undefined;
                                  item.tabs[tabIndex].subgroupId = undefined;
                                })
                              }
                            >
                              <option value="">
                                Definir pelo mapeamento dos registros
                              </option>
                              {config.groups
                                .filter((group) => !group.parentId)
                                .map((group) => (
                                  <option value={group.id} key={group.id}>
                                    {group.name}
                                  </option>
                                ))}
                            </select>
                          </Field>
                          <Field label="Subgrupo confirmado da aba">
                            <select
                              value={tab.subgroupId || ""}
                              onChange={(event) =>
                                sourceUpdate(index, (item) => {
                                  item.tabs[tabIndex].subgroupId =
                                    event.target.value || undefined;
                                })
                              }
                            >
                              <option value="">Sem associação fixa</option>
                              {config.groups
                                .filter(
                                  (group) =>
                                    group.parentId &&
                                    group.parentId === tab.groupId,
                                )
                                .map((group) => (
                                  <option value={group.id} key={group.id}>
                                    {group.name}
                                  </option>
                                ))}
                            </select>
                          </Field>
                        </div>
                        <details>
                          <summary>Colunas e validação dos cabeçalhos</summary>
                          <p className="muted">
                            Informe a letra da coluna e o cabeçalho esperado.
                            Campos não disponíveis devem ficar sem mapeamento.
                          </p>
                          {Object.entries(mappedFieldNames).map(
                            ([field, label]) => (
                              <div className="vo-mapping-grid" key={field}>
                                <Field label={label}>
                                  <input
                                    value={
                                      tab.columns[field as VisitMappedField] ||
                                      ""
                                    }
                                    placeholder="Coluna, ex.: A"
                                    onChange={(event) =>
                                      sourceUpdate(index, (item) => {
                                        item.tabs[tabIndex].columns[
                                          field as VisitMappedField
                                        ] =
                                          event.target.value
                                            .trim()
                                            .toUpperCase() || undefined;
                                      })
                                    }
                                  />
                                </Field>
                                <Field label={`Cabeçalho esperado: ${label}`}>
                                  <input
                                    value={
                                      tab.expectedHeaders[
                                        field as VisitMappedField
                                      ] || ""
                                    }
                                    onChange={(event) =>
                                      sourceUpdate(index, (item) => {
                                        item.tabs[tabIndex].expectedHeaders[
                                          field as VisitMappedField
                                        ] = event.target.value || undefined;
                                      })
                                    }
                                  />
                                </Field>
                              </div>
                            ),
                          )}
                        </details>
                        <TabValueMappings
                          tab={tab}
                          config={config}
                          users={users}
                          onChange={(changed) =>
                            update((next) => {
                              next.sources[index].tabs[tabIndex] = changed;
                              for (const field of [
                                "responsibleId",
                                "visitAuthorId",
                              ] as const)
                                for (const id of Object.values(
                                  changed.valueMappings?.[field] || {},
                                )) {
                                  const confirmed = users.find(
                                    (person) => person.id === id,
                                  );
                                  if (confirmed)
                                    next[
                                      field === "responsibleId"
                                        ? "responsibleNames"
                                        : "authorNames"
                                    ][id] = confirmed.name;
                                }
                            })
                          }
                        />
                        <Button
                          kind="danger-ghost"
                          onClick={() =>
                            sourceUpdate(index, (item) => {
                              item.tabs.splice(tabIndex, 1);
                            })
                          }
                        >
                          Retirar aba do rascunho
                        </Button>
                      </details>
                    ))}
                    <div className="vo-tools">
                      <Button
                        icon="plus"
                        onClick={() =>
                          sourceUpdate(index, (item) => {
                            item.tabs.push({
                              id: crypto.randomUUID(),
                              sheetId: 0,
                              title: "",
                              kind: "original",
                              headerRow: 1,
                              columns: {},
                              expectedHeaders: {},
                            });
                          })
                        }
                      >
                        Adicionar aba original
                      </Button>
                      <Button
                        disabled={dirty || !source.enabled || !!state.draft}
                        busy={busy === "sync:" + source.id}
                        icon="refresh"
                        onClick={() =>
                          run("sync:" + source.id, async () => {
                            const result = await api<{
                              state: "ready" | "failed" | "skipped";
                              error?: string;
                            }>(
                              path +
                                "/sources/" +
                                encodeURIComponent(source.id) +
                                "/sync",
                              "POST",
                            );
                            if (result.state === "failed")
                              throw new Error(
                                result.error ||
                                  "Não foi possível atualizar a fonte. Os dados anteriores continuam identificados na visão geral.",
                              );
                            notify?.(
                              result.state === "ready"
                                ? "Fonte atualizada. Consulte os dados na visão geral."
                                : "A fonte já está em atualização ou ainda aguarda o intervalo configurado.",
                            );
                          })
                        }
                      >
                        Atualizar fonte
                      </Button>
                      <Button
                        kind="danger-ghost"
                        onClick={() =>
                          update((next) => {
                            next.sources.splice(index, 1);
                          })
                        }
                      >
                        Desconectar no rascunho
                      </Button>
                    </div>
                    <small>Identificador interno: {source.id}</small>
                  </details>
                );
              })}
            {!config.sources.length && (
              <p>
                Nenhuma fonte configurada. Adicione uma fonte e confira os
                cabeçalhos antes de publicar.
              </p>
            )}
            <Button
              icon="plus"
              onClick={() =>
                update((next) => {
                  next.sources.push({
                    id: crypto.randomUUID(),
                    name: "",
                    spreadsheetId: "",
                    enabled: false,
                    tabs: [],
                    syncIntervalMinutes: 30,
                    maxRowsPerTab: 10000,
                  });
                })
              }
            >
              Adicionar fonte
            </Button>
          </div>
        )}
        {section === "groups" && (
          <div className="vo-surface">
            <h2>Grupos e associações confirmadas</h2>
            <p className="muted">
              Os grupos são cadastros geográficos. O nome da planilha não
              atribui responsabilidade. Alterar este mapeamento não move nem
              delega endereços.
            </p>
            {config.groups.map((group, index) => (
              <div className="vo-admin-source" key={group.id}>
                <Field label="Nome do grupo ou subgrupo">
                  <input
                    value={group.name}
                    onChange={(event) =>
                      update((next) => {
                        next.groups[index].name = event.target.value;
                      })
                    }
                  />
                </Field>
                <Field label="Grupo superior">
                  <select
                    value={group.parentId || ""}
                    onChange={(event) =>
                      update((next) => {
                        next.groups[index].parentId =
                          event.target.value || undefined;
                      })
                    }
                  >
                    <option value="">Grupo principal</option>
                    {config.groups
                      .filter((item) => item.id !== group.id && !item.parentId)
                      .map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name}
                        </option>
                      ))}
                  </select>
                </Field>
                <small>Identificador: {group.id}</small>
                <Button
                  kind="danger-ghost"
                  onClick={() =>
                    update((next) => {
                      next.groups.splice(index, 1);
                    })
                  }
                >
                  Retirar grupo do rascunho
                </Button>
              </div>
            ))}
            <Button
              icon="plus"
              onClick={() =>
                update((next) => {
                  next.groups.push({ id: crypto.randomUUID(), name: "" });
                })
              }
            >
              Adicionar grupo
            </Button>
            <details className="vo-help">
              <summary>
                Nomes de responsáveis e autores já identificados
              </summary>
              <p>
                Associe somente identificadores confirmados na origem ou no
                cadastro canônico. O texto de uma planilha não comprova essa
                associação.
              </p>
              {(["responsibleNames", "authorNames"] as const).map((key) => (
                <div key={key}>
                  <h3>
                    {key === "responsibleNames"
                      ? "Responsáveis atuais"
                      : "Autores das visitas"}
                  </h3>
                  {Object.entries(config[key]).map(([id, name]) => (
                    <div className="vo-mapping-grid" key={id}>
                      <Field label="Pessoa cadastrada">
                        <select
                          value={id}
                          onChange={(event) =>
                            update((next) => {
                              delete next[key][id];
                              next[key][event.target.value] =
                                users.find(
                                  (person) => person.id === event.target.value,
                                )?.name || name;
                            })
                          }
                        >
                          {!users.some((person) => person.id === id) && (
                            <option value={id}>
                              {name || "Associação anterior"}
                            </option>
                          )}
                          {users.map((person) => (
                            <option value={person.id} key={person.id}>
                              {person.name}
                            </option>
                          ))}
                        </select>
                      </Field>
                      <Field label="Nome de exibição">
                        <input
                          value={name}
                          onChange={(event) =>
                            update((next) => {
                              next[key][id] = event.target.value;
                            })
                          }
                        />
                      </Field>
                      <Button
                        kind="danger-ghost"
                        onClick={() =>
                          update((next) => {
                            delete next[key][id];
                          })
                        }
                      >
                        Retirar associação
                      </Button>
                    </div>
                  ))}
                  <Field label="Adicionar pessoa confirmada">
                    <select
                      value=""
                      onChange={(event) => {
                        if (event.target.value)
                          update((next) => {
                            next[key][event.target.value] =
                              users.find(
                                (person) => person.id === event.target.value,
                              )?.name || "";
                          });
                      }}
                    >
                      <option value="">Selecione a pessoa</option>
                      {users
                        .filter((person) => !(person.id in config[key]))
                        .map((person) => (
                          <option value={person.id} key={person.id}>
                            {person.name}
                          </option>
                        ))}
                    </select>
                  </Field>
                </div>
              ))}
            </details>
          </div>
        )}
        {section === "permissions" && user.admin && (
          <div className="vo-surface">
            <h2>Acesso individual</h2>
            <p className="muted">
              Consultar não concede edição, transferência, exclusão ou registro
              de visita. A permissão de exportar PDF é independente.
            </p>
            {users
              .filter(
                (item) =>
                  !configSearch ||
                  item.name
                    .toLocaleLowerCase("pt-BR")
                    .includes(configSearch.toLocaleLowerCase("pt-BR")),
              )
              .map((person) => (
                <details className="vo-admin-source" key={person.id}>
                  <summary>
                    {person.name}
                    {person.id === user.id ? " · Você" : ""}
                    {!person.active
                      ? " · Conta inativa"
                      : person.admin
                        ? " · Administrador"
                        : config.councillorUserIds.includes(person.id)
                          ? " · Vereador"
                          : ""}
                  </summary>
                  {!person.active && (
                    <p className="muted">
                      Conta inativa: nenhum acesso está disponível. As
                      autorizações cadastradas permanecem guardadas para
                      eventual reativação pelo administrador.
                    </p>
                  )}
                  <label className="vo-admin-toggle">
                    <input
                      type="checkbox"
                      checked={config.councillorUserIds.includes(person.id)}
                      onChange={(event) =>
                        update((next) => {
                          next.councillorUserIds = event.target.checked
                            ? [
                                ...new Set([
                                  ...next.councillorUserIds,
                                  person.id,
                                ]),
                              ]
                            : next.councillorUserIds.filter(
                                (id) => id !== person.id,
                              );
                        })
                      }
                    />
                    <span>
                      Vereador
                      <small>
                        Confirme o cadastro do papel. Vereador e administrador
                        preservam seus poderes existentes.
                      </small>
                    </span>
                  </label>
                  {Object.entries(capabilityNames).map(([capability, name]) => {
                    const inherited =
                      person.admin ||
                      (config.councillorUserIds.includes(person.id) &&
                        !["export", "configure"].includes(capability));
                    const canAccess =
                      person.active &&
                      (person.admin ||
                        config.councillorUserIds.includes(person.id) ||
                        !!config.grants[person.id]?.access);
                    return (
                      <label className="vo-admin-toggle" key={capability}>
                        <input
                          type="checkbox"
                          disabled={
                            !person.active ||
                            inherited ||
                            (capability !== "access" && !canAccess)
                          }
                          checked={
                            canAccess &&
                            (inherited ||
                              !!config.grants[person.id]?.[
                                capability as VisitCapability
                              ])
                          }
                          onChange={(event) =>
                            update((next) => {
                              next.grants[person.id] = {
                                ...next.grants[person.id],
                                [capability]: event.target.checked,
                              };
                            })
                          }
                        />
                        <span>
                          {name}
                          {inherited && (
                            <small>
                              Acesso herdado do papel de{" "}
                              {person.admin ? "administrador" : "vereador"}.
                            </small>
                          )}
                          {!inherited &&
                            capability !== "access" &&
                            !canAccess &&
                            person.active && (
                              <small>
                                Primeiro autorize o acesso à visão consolidada.
                              </small>
                            )}
                        </span>
                      </label>
                    );
                  })}
                </details>
              ))}
          </div>
        )}
        {section === "updates" && (
          <div className="vo-surface">
            <h2>Atualização e limites</h2>
            <Field label="Considerar dados desatualizados após (minutos)">
              <input
                type="number"
                min={5}
                max={43200}
                value={config.staleAfterMinutes}
                onChange={(event) =>
                  update((next) => {
                    next.staleAfterMinutes = Number(event.target.value);
                  })
                }
              />
            </Field>
            <Field label="Política de cache registrada (minutos)">
              <input
                type="number"
                min={0}
                max={1440}
                value={config.cacheMinutes}
                readOnly
                disabled
              />
            </Field>
            <p className="muted">
              Cache de consultas com expiração ainda não está disponível. A
              consulta atual usa o banco persistente; este valor não ativa um
              cache separado.
            </p>
            <p className="muted">
              Esta área não baixa automaticamente cadastros pessoais para uso
              offline. Fontes com falha permanecem identificadas; não são
              convertidas em zero.
            </p>
            <details className="vo-help">
              <summary>Indicadores legados</summary>
              <p>
                POSITIVO exige interpretação de negócio validada. DELETADOS e
                Deslocados manuais não são eventos confirmados do aplicativo.
                Eles não alteram visitas, pontos, XP ou metas.
              </p>
            </details>
          </div>
        )}
        {section === "versions" && (
          <div className="vo-surface">
            <h2>Versões e comparação</h2>
            <details className="vo-help">
              <summary>Salvar um retrato dos indicadores atuais</summary>
              <Field label="Nome do retrato">
                <input
                  value={snapshotName}
                  onChange={(event) => setSnapshotName(event.target.value)}
                  maxLength={120}
                  placeholder="Referência e data"
                />
              </Field>
              <Button
                disabled={!snapshotName.trim() || dirty || !!state.draft}
                onClick={() =>
                  run("snapshot", async () => {
                    await api(path + "/snapshots", "POST", {
                      name: snapshotName.trim(),
                      query: {},
                    });
                    setSnapshotName("");
                    notify?.(
                      "Retrato salvo. Ele poderá ser comparado com referências compatíveis.",
                    );
                  })
                }
              >
                Salvar retrato
              </Button>
              <p className="muted">
                Registra a situação da base publicada. Variações entre retratos
                não representam automaticamente visitas no intervalo.
              </p>
            </details>
            <p>Configuração em edição: versão {currentVersion}.</p>
            <details>
              <summary>Comparar com a publicação atual</summary>
              <div className="vo-compare-grid">
                <div>
                  <h3>Publicada</h3>
                  <ConfigPreview config={state.published} />
                </div>
                <div>
                  <h3>Rascunho em edição</h3>
                  <ConfigPreview config={config} />
                </div>
              </div>
            </details>
            {versions.map((version) => (
              <article className="vo-origin" key={version.version}>
                <strong>
                  Versão {version.version} ·{" "}
                  {version.state === "published" ? "Publicada" : "Rascunho"}
                </strong>
                <p>
                  {date(version.at)} · {version.config.sources.length} fontes
                </p>
                <Button
                  disabled={
                    dirty || version.version === state.published.version
                  }
                  onClick={() =>
                    run("restore", async () => {
                      await api(path + "/config/restore", "POST", {
                        version: version.version,
                        expectedVersion: state.published.version,
                        expectedDraftAt: state.draft?.at ?? null,
                      });
                      await load();
                      notify?.(
                        "Versão preparada como rascunho. Valide e publique para aplicar.",
                      );
                    })
                  }
                >
                  Restaurar como rascunho
                </Button>
              </article>
            ))}
          </div>
        )}
      </fieldset>
    </section>
  );
}

function ConfigPreview({ config }: { config: VisitsConfig }) {
  return (
    <dl className="vo-definition">
      <dt>Fontes incluídas</dt>
      <dd>
        {config.sources
          .filter((source) => source.enabled)
          .map((source) => source.name)
          .join(" · ") || "Nenhuma"}
      </dd>
      <dt>Abas originais</dt>
      <dd>
        {config.sources.reduce(
          (total, source) => total + source.tabs.length,
          0,
        )}
      </dd>
      <dt>Grupos cadastrados</dt>
      <dd>{config.groups.length}</dd>
      <dt>Autorizações individuais</dt>
      <dd>{Object.keys(config.grants).length}</dd>
      <dt>Dados desatualizados</dt>
      <dd>Após {config.staleAfterMinutes} minutos</dd>
    </dl>
  );
}

function TabValueMappings({
  tab,
  config,
  users,
  onChange,
}: {
  tab: VisitTab;
  config: VisitsConfig;
  users: Array<{ id: string; name: string }>;
  onChange: (tab: VisitTab) => void;
}) {
  const update = (fn: (next: VisitTab) => void) => {
    const next = structuredClone(tab);
    fn(next);
    onChange(next);
  };
  const names = {
    group: "Grupo",
    subgroup: "Subgrupo",
    responsibleId: "Responsável atual",
    visitAuthorId: "Autor da visita",
  };
  return (
    <details className="vo-help">
      <summary>Correspondências de valores, situação e datas</summary>
      <p className="muted">
        Uma correspondência associa o valor existente na planilha a um cadastro
        confirmado. Ela não concede ou transfere responsabilidade operacional.
      </p>
      {Object.entries(names).map(([field, name]) => {
        const key = field as keyof typeof names;
        const mapping = tab.valueMappings?.[key] || {};
        const options =
          key === "group"
            ? config.groups.map((group) => ({
                ...group,
                name: group.parentId
                  ? `${config.groups.find((parent) => parent.id === group.parentId)?.name ?? "Grupo"} · ${group.name}`
                  : group.name,
              }))
            : key === "subgroup"
              ? config.groups
                  .filter((group) => !!group.parentId)
                  .map((group) => ({
                    ...group,
                    name: `${config.groups.find((parent) => parent.id === group.parentId)?.name ?? "Grupo"} · ${group.name}`,
                  }))
              : users.length
                ? users
                : Object.entries(
                    config[
                      key === "responsibleId"
                        ? "responsibleNames"
                        : "authorNames"
                    ],
                  ).map(([id, name]) => ({ id, name }));
        return (
          <section className="vo-admin-source" key={key}>
            <h3>{name}</h3>
            {Object.entries(mapping).map(([source, target], index) => (
              <div className="vo-mapping-grid" key={index}>
                <Field label="Valor na planilha">
                  <input
                    value={source}
                    onChange={(event) =>
                      update((next) => {
                        next.valueMappings ??= {};
                        next.valueMappings[key] ??= {};
                        delete next.valueMappings[key]![source];
                        next.valueMappings[key]![event.target.value] = target;
                      })
                    }
                  />
                </Field>
                <Field label="Cadastro confirmado">
                  <select
                    value={target}
                    onChange={(event) =>
                      update((next) => {
                        next.valueMappings ??= {};
                        next.valueMappings[key] ??= {};
                        next.valueMappings[key]![source] = event.target.value;
                      })
                    }
                  >
                    <option value="">Selecione</option>
                    {!options.some((option) => option.id === target) &&
                      !!target && (
                        <option value={target}>
                          Associação anterior — conferir
                        </option>
                      )}
                    {options.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.name}
                      </option>
                    ))}
                  </select>
                </Field>
                <Button
                  kind="danger-ghost"
                  onClick={() =>
                    update((next) => {
                      delete next.valueMappings?.[key]?.[source];
                    })
                  }
                >
                  Retirar correspondência
                </Button>
              </div>
            ))}
            <Button
              icon="plus"
              disabled={"" in mapping}
              onClick={() =>
                update((next) => {
                  next.valueMappings ??= {};
                  next.valueMappings[key] ??= {};
                  next.valueMappings[key]![""] = "";
                })
              }
            >
              Adicionar correspondência de {name.toLocaleLowerCase("pt-BR")}
            </Button>
          </section>
        );
      })}
      <Field label="Formato das datas na origem">
        <select
          value={tab.dateFormat || "iso"}
          onChange={(event) =>
            update((next) => {
              next.dateFormat = event.target.value as "iso" | "dmy";
            })
          }
        >
          <option value="iso">Ano-mês-dia (ISO)</option>
          <option value="dmy">Dia/mês/ano</option>
        </select>
      </Field>
      <p className="muted">
        A primeira visita só deve ser mapeada quando a origem comprovar esse
        significado. A data mais recente não reconstrói a primeira visita.
      </p>
      {(["visited", "pending", "analysis"] as const).map((status) => (
        <Field
          key={status}
          label={`Valores que significam “${statusNames[status]}”`}
          hint="Um valor por linha, somente conforme a interpretação confirmada da origem."
        >
          <textarea
            rows={2}
            value={(tab.statusValues?.[status] || []).join("\n")}
            onChange={(event) =>
              update((next) => {
                next.statusValues ??= {
                  visited: [],
                  pending: [],
                  analysis: [],
                };
                next.statusValues[status] = event.target.value.split("\n");
              })
            }
          />
        </Field>
      ))}
    </details>
  );
}

